
import { Story, Author, Comment } from '@/utils/storiesData';
import { 
  ContentfulAuthorFields, 
  ContentfulCommentFields,
  ContentfulStoryFields,
  ContentfulTag
} from './contentfulTypes';

// Transform Contentful entries to our app's data model
export const transformAuthor = (authorFields: ContentfulAuthorFields | undefined): Author => {
  // If author fields don't exist, return a default author
  if (!authorFields) {
    return {
      id: 'default-author',
      name: 'Default Author',
      avatar: '/lovable-uploads/0478f0f4-6d1c-4e90-b08a-6bc2b74cd16c.png',
      bio: 'No bio available'
    };
  }
  
  return {
    id: Math.random().toString(36).substring(2, 9), // Generate an ID
    name: authorFields.name || 'Anonymous',
    avatar: authorFields.avatar ? `https:${authorFields.avatar.fields.file.url}` : '/lovable-uploads/0478f0f4-6d1c-4e90-b08a-6bc2b74cd16c.png',
    bio: authorFields.bio || 'No bio available',
  };
};

export const transformComment = (commentFields: ContentfulCommentFields | undefined): Comment => {
  if (!commentFields) {
    return {
      id: 'default-comment',
      content: 'No comment content',
      author: transformAuthor(undefined),
      createdAt: new Date().toISOString(),
      replies: [],
    };
  }

  return {
    id: Math.random().toString(36).substring(2, 9),
    content: commentFields.content,
    author: transformAuthor(commentFields.author?.fields),
    createdAt: commentFields.createdAt || new Date().toISOString(),
    replies: commentFields.replies 
      ? commentFields.replies.map(reply => transformComment(reply.fields)) 
      : [],
  };
};

// Helper function to extract image URLs from Contentful Asset arrays
export const extractContentfulImageUrls = (images: any[] | undefined): string[] => {
  if (!images || !Array.isArray(images)) {
    return [];
  }
  
  return images.map(imageEntry => {
    if (imageEntry && imageEntry.fields && imageEntry.fields.file && imageEntry.fields.file.url) {
      return `https:${imageEntry.fields.file.url}`;
    }
    return '';
  }).filter(url => url !== '');
};

// Process content to extract inline image references
export const processContentfulContent = (content: string): string => {
  return content.replace(/\[contentful-image:([\w-]+)\]/g, (match, fieldId) => {
    return match;
  });
};

// Extract and normalize tags from contentful entry
export const extractContentfulTags = (entry: any): string[] => {
  const tags: string[] = [];
  
  // Try to extract tags from contentfulMetadata
  if (entry.metadata && entry.metadata.tags && Array.isArray(entry.metadata.tags)) {
    entry.metadata.tags.forEach((tag: any) => {
      if (tag && tag.sys && tag.sys.id) {
        tags.push(tag.sys.id);
      }
    });
  }
  
  // Try fields.tags
  if (entry.fields && entry.fields.tags) {
    const fieldTags = entry.fields.tags;
    if (Array.isArray(fieldTags)) {
      fieldTags.forEach((tag: any) => {
        if (typeof tag === 'string') {
          tags.push(tag);
        } else if (tag && typeof tag === 'object' && tag !== null) {
          if ('name' in tag && typeof tag.name === 'string') {
            tags.push(tag.name);
          } else if ('sys' in tag && tag.sys && typeof tag.sys === 'object' && 
                     'id' in tag.sys && typeof tag.sys.id === 'string') {
            tags.push(tag.sys.id);
          }
        }
      });
    } else if (typeof fieldTags === 'string') {
      // If it's a comma-separated string, split it
      if (fieldTags.includes(',')) {
        fieldTags.split(',').map(tag => tag.trim()).filter(Boolean).forEach(tag => tags.push(tag));
      } else {
        tags.push(fieldTags);
      }
    }
  }
  
  // Check contentfulMetadata directly
  if (entry.sys && entry.sys.contentfulMetadata && 
      entry.sys.contentfulMetadata.tags && 
      Array.isArray(entry.sys.contentfulMetadata.tags)) {
    entry.sys.contentfulMetadata.tags.forEach((tag: any) => {
      if (tag && tag.sys && tag.sys.id) {
        tags.push(tag.sys.id);
      }
    });
  }
  
  // Remove duplicates
  return [...new Set(tags)];
};

export const transformStory = (entry: any): Story => {
  const fields = entry.fields as ContentfulStoryFields;
  
  const processedContent = fields.content ? processContentfulContent(fields.content) : '';
  
  const imageUrls = extractContentfulImageUrls(fields.images);
  const galleryUrls = extractContentfulImageUrls(fields.gallery);
  
  let vimeoId = fields.vimeo || null;
  
  // Get tags using the updated extraction function
  const tags = extractContentfulTags(entry);
  
  console.log('Story tags found:', tags);
  
  return {
    id: entry.sys.id,
    slug: fields.slug || `story-${entry.sys.id}`,
    title: fields.title || 'Untitled Story',
    subtitle: fields.subtitle || '',
    coverImage: fields.coverImage 
      ? `https:${fields.coverImage.fields.file.url}` 
      : '/lovable-uploads/0478f0f4-6d1c-4e90-b08a-6bc2b74cd16c.png',
    excerpt: fields.excerpt || 'No excerpt available',
    content: processedContent || 'No content available',
    author: transformAuthor(fields.author?.fields),
    publishedAt: fields.publishedAt || new Date().toISOString(),
    category: fields.category || 'Uncategorized',
    readTime: fields.readTime || 5,
    tags: tags, 
    featured: fields.featured || false,
    comments: fields.comments 
      ? fields.comments.map(comment => transformComment(comment.fields)) 
      : [],
    vimeo: vimeoId,
  };
};
